/* Control de EPP: stock por depósito.
 *
 * Saldo por ítem y talle, ingreso de stock en cada reposición (con remito o
 * factura), transferencia a otro depósito, ajuste con motivo (solo SSHO) y los
 * movimientos del depósito, entregas incluidas. */
import { IonButton, IonContent, IonIcon, IonPage, IonSegment, IonSegmentButton, IonLabel } from "@ionic/react";
import { Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { addOutline, swapHorizontalOutline, trashOutline, createOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import Header from "src/components/Header";
import { Cargando, SelectorDeposito, useRolEpp, useToastEpp } from "src/components/epp/EppComunes";
import EppAPI, { Item, Movimiento, Saldo, TipoMovimiento, etiquetaItem, fechaCorta } from "src/services/epp";
import "./Epp.css";

type Operacion = "INGRESO" | "TRANSFERENCIA" | "AJUSTE";

interface LineaForm {
  clave: number;
  itemId: number | null;
  talle: string;
  cantidad: string;
}

let ultimaClave = 0;
const lineaVacia = (): LineaForm => ({ clave: ++ultimaClave, itemId: null, talle: "", cantidad: "" });

const ETIQUETA_MOVIMIENTO: Record<TipoMovimiento, string> = {
  INGRESO: "Ingreso",
  TRANSFERENCIA: "Transferencia",
  AJUSTE: "Ajuste",
  ENTREGA: "Entrega",
};

const EppStock: React.FC = () => {
  const rol = useRolEpp();
  const toast = useToastEpp();
  const [depositoId, setDepositoId] = useState<number | "">("");
  const [vista, setVista] = useState<"saldo" | "movimientos">("saldo");
  const [stock, setStock] = useState<Saldo[] | null>(null);
  const [movimientos, setMovimientos] = useState<Movimiento[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [destinos, setDestinos] = useState<{ Id: number; Nombre: string }[]>([]);

  const [operacion, setOperacion] = useState<Operacion | null>(null);
  const [lineas, setLineas] = useState<LineaForm[]>([lineaVacia()]);
  const [texto, setTexto] = useState("");
  const [destinoId, setDestinoId] = useState<number | "">("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (rol?.depositos.length && depositoId === "") setDepositoId(rol.depositos[0].Id);
  }, [rol, depositoId]);

  useEffect(() => {
    EppAPI.items().then(setItems).catch((err) => toast.error(err, "No se pudo cargar el catálogo"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cargar = async () => {
    if (!depositoId) return;
    setStock(null);
    setMovimientos(null);
    try {
      const [s, m] = await Promise.all([EppAPI.stock(depositoId), EppAPI.movimientos(depositoId)]);
      setStock(s);
      setMovimientos(m);
    } catch (err) {
      setStock([]);
      setMovimientos([]);
      toast.error(err, "No se pudo cargar el stock");
    }
  };

  useEffect(() => {
    cargar();
  }, [depositoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemPorId = useMemo(() => Object.fromEntries(items.map((i) => [i.Id, i])), [items]);
  const total = (stock || []).reduce((a, s) => a + s.Cantidad, 0);

  const abrir = async (op: Operacion) => {
    setOperacion(op);
    setLineas([lineaVacia()]);
    setTexto("");
    setDestinoId("");
    if (op === "TRANSFERENCIA") {
      // El destino puede ser cualquier depósito activo, aunque no se tenga a cargo.
      try {
        setDestinos((await EppAPI.destinos()).filter((d) => d.Id !== depositoId));
      } catch (err) {
        toast.error(err, "No se pudieron cargar los depósitos");
      }
    }
  };

  const cambiarLinea = (clave: number, cambios: Partial<LineaForm>) =>
    setLineas((ls) => ls.map((l) => (l.clave === clave ? { ...l, ...cambios } : l)));

  /** Ítems que se pueden elegir: en ingreso y ajuste el catálogo; en transferencia lo que hay en stock. */
  const opcionesItem = (op: Operacion): Item[] =>
    op === "TRANSFERENCIA" ? items.filter((i) => (stock || []).some((s) => s.ItemId === i.Id && s.Cantidad > 0)) : items;

  const guardar = async () => {
    if (!depositoId || !operacion) return;
    const payload = lineas
      .filter((l) => l.itemId)
      .map((l) => ({ ItemId: l.itemId as number, Talle: l.talle.trim().toUpperCase(), Cantidad: Number(l.cantidad) }));
    if (!payload.length) return toast.aviso("Agregá al menos un ítem");
    for (const l of payload) {
      const item = itemPorId[l.ItemId];
      const valida = operacion === "AJUSTE" ? Number.isInteger(l.Cantidad) && l.Cantidad !== 0 : Number.isInteger(l.Cantidad) && l.Cantidad > 0;
      if (!valida) return toast.aviso(`Revisá la cantidad de ${item?.Nombre}`);
      if (item?.UsaTalle && !l.Talle) return toast.aviso(`Indicá el talle de ${item.Nombre}`);
    }
    if (operacion === "AJUSTE" && !texto.trim()) return toast.aviso("El motivo del ajuste es obligatorio");
    if (operacion === "TRANSFERENCIA" && !destinoId) return toast.aviso("Elegí el depósito de destino");

    setGuardando(true);
    try {
      if (operacion === "INGRESO") {
        await EppAPI.ingreso({ DepositoId: depositoId, Referencia: texto.trim() || undefined, Lineas: payload });
      } else if (operacion === "TRANSFERENCIA") {
        await EppAPI.transferencia({
          DepositoId: depositoId,
          DepositoDestinoId: destinoId as number,
          Observacion: texto.trim() || undefined,
          Lineas: payload,
        });
      } else {
        await EppAPI.ajuste({ DepositoId: depositoId, ...payload[0], Motivo: texto.trim() });
      }
      toast.ok("Movimiento registrado");
      setOperacion(null);
      cargar();
    } catch (err) {
      toast.error(err, "No se pudo registrar el movimiento");
    } finally {
      setGuardando(false);
    }
  };

  if (!rol) {
    return (
      <IonPage>
        <Header titulo="Stock de EPP" />
        <IonContent><Cargando /></IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header titulo="Stock de EPP" />
      <IonContent id="epp-stock">
        <div className="epp-page campos-mui">
          <div className="epp-barra">
            <SelectorDeposito depositos={rol.depositos} valor={depositoId} onChange={setDepositoId} />
          </div>
          <div className="epp-barra">
            <IonButton onClick={() => abrir("INGRESO")} disabled={!depositoId}>
              <IonIcon slot="start" icon={addOutline} /> Ingreso
            </IonButton>
            <IonButton fill="outline" onClick={() => abrir("TRANSFERENCIA")} disabled={!depositoId || !total}>
              <IonIcon slot="start" icon={swapHorizontalOutline} /> Transferir
            </IonButton>
            {rol.admin && (
              <IonButton fill="outline" onClick={() => abrir("AJUSTE")} disabled={!depositoId}>
                <IonIcon slot="start" icon={createOutline} /> Ajuste
              </IonButton>
            )}
          </div>

          <IonSegment value={vista} onIonChange={(e) => setVista((e.detail.value as any) || "saldo")}>
            <IonSegmentButton value="saldo"><IonLabel>Saldo ({total})</IonLabel></IonSegmentButton>
            <IonSegmentButton value="movimientos"><IonLabel>Movimientos</IonLabel></IonSegmentButton>
          </IonSegment>

          {vista === "saldo" && (
            stock === null ? <Cargando /> : (
              <div className="epp-card" style={{ marginTop: 12 }}>
                {stock.length === 0 && <div className="epp-vacio">El depósito no tiene stock.</div>}
                {stock.map((s) => (
                  <div key={`${s.ItemId}|${s.Talle}`} className="epp-fila" style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                      <div className="epp-item">{etiquetaItem(s.Item, s.Talle)}</div>
                      <div className="epp-meta">{s.VidaUtilDias ? `Vida útil ${s.VidaUtilDias} días` : "Sin vida útil definida"}</div>
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{s.Cantidad}</div>
                  </div>
                ))}
              </div>
            )
          )}

          {vista === "movimientos" && (
            movimientos === null ? <Cargando /> : (
              <div className="epp-card" style={{ marginTop: 12 }}>
                {movimientos.length === 0 && <div className="epp-vacio">Sin movimientos.</div>}
                {movimientos.map((m, i) => (
                  <div key={i} className={`epp-fila ${m.Anulada ? "epp-anulada" : ""}`}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                      <span>
                        <strong>{ETIQUETA_MOVIMIENTO[m.Tipo]}</strong> · {etiquetaItem(m.Item, m.Talle)}
                      </span>
                      <strong style={{ color: m.Cantidad > 0 ? "#166534" : "#991b1b" }}>
                        {m.Cantidad > 0 ? `+${m.Cantidad}` : m.Cantidad}
                      </strong>
                    </div>
                    <div className="epp-meta">
                      <span>{fechaCorta(m.Fecha)} {m.Fecha.slice(11, 16)}</span>
                      {m.Contraparte && (
                        <span>
                          {m.Tipo === "ENTREGA" ? "A " : m.Cantidad > 0 ? "Desde " : "Hacia "}
                          {m.Contraparte}
                        </span>
                      )}
                      <span>Por {m.UsuarioNombre}</span>
                      {m.Anulada && <span>Entrega anulada</span>}
                    </div>
                    {m.Observacion && <div className="epp-meta">{m.Observacion}</div>}
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        <Dialog className="epp-dialogo" open={operacion !== null} onClose={() => !guardando && setOperacion(null)} fullWidth maxWidth="sm">
          <DialogTitle>
            {operacion === "INGRESO" ? "Ingreso de stock" : operacion === "TRANSFERENCIA" ? "Transferir a otro depósito" : "Ajuste de stock"}
          </DialogTitle>
          <DialogContent className="campos-mui">
            {operacion === "TRANSFERENCIA" && (
              <TextField
                select
                label="Depósito de destino"
                variant="outlined"
                fullWidth
                margin="dense"
                value={destinoId}
                onChange={(e) => setDestinoId(Number(e.target.value))}
                helperText={destinos.length ? undefined : "No hay otro depósito activo"}
              >
                {destinos.map((d) => (
                  <MenuItem key={d.Id} value={d.Id}>{d.Nombre}</MenuItem>
                ))}
              </TextField>
            )}
            {operacion && lineas.map((l) => {
              const item = l.itemId ? itemPorId[l.itemId] : null;
              const enStock = (stock || []).filter((s) => s.ItemId === l.itemId);
              return (
                <div key={l.clave} className="epp-fila">
                  <MuiAutocomplete
                    options={opcionesItem(operacion)}
                    value={item}
                    getOptionLabel={(i) => i.Nombre}
                    getOptionSelected={(a, b) => a.Id === b.Id}
                    onChange={(_, i) => cambiarLinea(l.clave, { itemId: i?.Id ?? null, talle: "" })}
                    renderInput={(params) => <TextField {...params} label="Ítem" variant="outlined" size="small" />}
                  />
                  <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                    {item?.UsaTalle && (
                      operacion === "INGRESO" ? (
                        <TextField
                          label="Talle"
                          variant="outlined"
                          size="small"
                          value={l.talle}
                          inputProps={{ maxLength: 20 }}
                          onChange={(e) => cambiarLinea(l.clave, { talle: e.target.value })}
                        />
                      ) : (
                        <TextField
                          select
                          label="Talle"
                          variant="outlined"
                          size="small"
                          style={{ minWidth: 110 }}
                          value={l.talle}
                          onChange={(e) => cambiarLinea(l.clave, { talle: String(e.target.value) })}
                        >
                          {enStock.map((s) => (
                            <MenuItem key={s.Talle} value={s.Talle}>{s.Talle} ({s.Cantidad})</MenuItem>
                          ))}
                        </TextField>
                      )
                    )}
                    <TextField
                      label={operacion === "AJUSTE" ? "Cantidad (+/-)" : "Cantidad"}
                      type="number"
                      variant="outlined"
                      size="small"
                      style={{ width: 130 }}
                      value={l.cantidad}
                      onChange={(e) => cambiarLinea(l.clave, { cantidad: e.target.value })}
                      helperText={
                        operacion !== "INGRESO" && item
                          ? `Hay ${enStock.find((s) => s.Talle === (item.UsaTalle ? l.talle.toUpperCase() : ""))?.Cantidad ?? 0}`
                          : undefined
                      }
                    />
                    {operacion !== "AJUSTE" && lineas.length > 1 && (
                      <IonButton fill="clear" color="danger" onClick={() => setLineas((ls) => ls.filter((x) => x.clave !== l.clave))}>
                        <IonIcon icon={trashOutline} />
                      </IonButton>
                    )}
                  </div>
                </div>
              );
            })}
            {operacion !== "AJUSTE" && (
              <IonButton size="small" fill="clear" onClick={() => setLineas((ls) => [...ls, lineaVacia()])}>
                <IonIcon slot="start" icon={addOutline} /> Otro ítem
              </IonButton>
            )}
            <TextField
              label={operacion === "INGRESO" ? "Remito / factura (opcional)" : operacion === "AJUSTE" ? "Motivo (obligatorio)" : "Observación (opcional)"}
              variant="outlined"
              fullWidth
              margin="dense"
              value={texto}
              inputProps={{ maxLength: 300 }}
              onChange={(e) => setTexto(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <IonButton fill="clear" onClick={() => setOperacion(null)} disabled={guardando}>Cancelar</IonButton>
            <IonButton onClick={guardar} disabled={guardando}>{guardando ? "Guardando..." : "Guardar"}</IonButton>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  );
};

export default EppStock;
